import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AppEnv } from '@/types/enum';

/**
 * joydata 子项目已从主站移除，访问 /app/joydata 时重定向到对应环境的独立域名。
 *
 * URL 编码规则（与 micro-app 框架保持一致）：
 *   micro-app 在路由同步时对子应用路径做了自定义占位符编码，再整体 encodeURIComponent：
 *     原始字符  →  micro-app 占位符  →  URL 中呈现
 *       `=`    →      %M2          →    %25M2
 *       `&`    →      %M1          →    %25M1
 *       `?`    →      %M3          →    %25M3（部分场景）
 *
 *   反向解码步骤：
 *     1. URLSearchParams.get() 自动 decode 一次：标准 %XX 序列还原
 *        例：%2F → /，%3F → ?，%25M2 → %M2
 *     2. 替换 micro-app 自定义占位符：%M2 → =，%M1 → &，%M3 → ?
 *
 * 示例（ready 环境）：
 *   输入：joy-ready.123u.com/app/joydata?joydata=%2Fdashboard%3FprojectId%25M295%25M1dashboard_id%25M2private-3404318
 *   步骤1 decode：/dashboard?projectId%M295%M1dashboard_id%M2private-3404318
 *   步骤2 替换：/dashboard?projectId=95&dashboard_id=private-3404318
 *   输出：joydata-ready.123u.com/dashboard?projectId=95&dashboard_id=private-3404318
 */

const JOYDATA_URL_MAP: Record<AppEnv, string> = {
  [AppEnv.dev]: 'http://localhost:8899',
  [AppEnv.ready]: 'https://joydata-ready.123u.com',
  [AppEnv.pre]: 'https://joydata-pre.123u.com',
  [AppEnv.prd]: 'https://joydata.123u.com',
};

/**
 * 将 micro-app 自定义编码占位符还原为原始字符
 *   %M2 → =，%M1 → &，%M3 → ?
 */
function decodeMicroAppPlaceholders(str: string): string {
  return str
    .replace(/%M2/g, '=')
    .replace(/%M1/g, '&')
    .replace(/%M3/g, '?');
}

export default function JoydataRedirect() {
  const location = useLocation();

  useEffect(() => {
    const env = (import.meta.env.VITE_APP_ENV as AppEnv) ?? AppEnv.prd;
    const baseUrl = JOYDATA_URL_MAP[env] ?? JOYDATA_URL_MAP[AppEnv.prd];

    // URLSearchParams.get() 自动将标准 URL 编码 decode 一次
    // %2F → /，%3F → ?，%25M2 → %M2 等
    const searchParams = new URLSearchParams(location.search);
    let joydataPath = searchParams.get('joydata') ?? '/';

    // 收集除 joydata 以外的其余参数
    // 针对特殊格式链接（如 ?joydata=/shot?dashboard_id=normal-xxx&mobile_push=1&projectId=82）：
    // URLSearchParams 会将 & 后的 key-value 解析为独立的顶层参数，需将其重新拼回 joydata 路径
    const extraParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
      if (key !== 'joydata') {
        extraParams.append(key, value);
      }
    });

    // 替换 micro-app 自定义编码占位符，还原 =、& 等字符
    joydataPath = decodeMicroAppPlaceholders(joydataPath);

    if (!joydataPath) joydataPath = '/';

    // 若存在额外参数，拼接到 joydata 路径（兼容路径本身已含 ? 或不含 ? 两种情况）
    const extraStr = extraParams.toString();
    if (extraStr) {
      joydataPath = joydataPath.includes('?')
        ? `${joydataPath}&${extraStr}`
        : `${joydataPath}?${extraStr}`;
    }

    // 确保路径以 / 开头，再拼接到目标域名
    const normalizedPath = joydataPath.startsWith('/') ? joydataPath : `/${joydataPath}`;
    const targetUrl = `${baseUrl}${normalizedPath}`;

    window.location.replace(targetUrl);
  }, [location.search]);

  return null;
}
